import { startOfHour } from "date-fns";

import { findSeiches } from "@/features/dashboard/components/warnings/maritime-forecast-phenomena.utils";
import type { MaritimeArea } from "@/features/dashboard/components/warnings/maritime-areas.queries";
import {
  MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS,
  type MaritimeForecastShortRangePeriodKey,
} from "@/features/dashboard/components/warnings/maritime-forecast-period-keys";
import {
  normalizeSeichesSubtypeLabel,
  resolveSeichesMessage,
} from "@/features/shared/widgets/seiches/seiches-display.utils";
import type {
  MaritimeForecast,
  MaritimeForecastComponent,
  MaritimeForecastComponentOneOf3StatusEnum,
  MaritimeForecastTimeRangePeriod,
} from "@/lib/api";
import type { AppLocale } from "@/lib/locale/resolve-effective-locale";
import { Serie } from "@/types/serie";
import TimeRange from "@/types/time-range";

const SEICHES_TIME_RESOLUTION = "PT12H";

/** Aligns forecast period starts to hourly columns used by TimeSeriesTable. */
export function periodStartToTableKey(isoStart: string): string {
  return startOfHour(new Date(isoStart)).toISOString();
}

function seichesStatusToValue(status: MaritimeForecastComponentOneOf3StatusEnum): number {
  return status === "applicable" ? 1 : 0;
}

function readTimeRangePeriod(
  forecast: MaritimeForecast,
  periodKey: MaritimeForecastShortRangePeriodKey,
): MaritimeForecastTimeRangePeriod | null {
  const period = forecast.periods[periodKey];
  if (!period || period.type !== "time_range") return null;
  return period;
}

export type MaritimeSeichesPeriodCell = {
  value: number;
  message?: string;
  /** When value is applicable (1): normalized A–D label, or null when subtype is absent/invalid. */
  applicableSubtype?: string | null;
};

export type MaritimeSeichesViewModel = {
  series: Serie[];
  timeRange: TimeRange;
  cellsByPeriodStart: Record<string, MaritimeSeichesPeriodCell>;
};

/** Maps coastal forecast seiches for one area to TimeSeriesTable input and per-period cell metadata. */
export function buildMaritimeSeichesViewModel(
  forecast: MaritimeForecast,
  areaId: string,
  areaName: string,
  locale: AppLocale,
): MaritimeSeichesViewModel | null {
  const forecastArea = forecast.areas.find((a) => a.area_id === areaId);
  if (!forecastArea) return null;

  const hours0_12 = readTimeRangePeriod(
    forecast,
    MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS[0],
  );
  const hours12_24 = readTimeRangePeriod(
    forecast,
    MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS[1],
  );
  if (!hours0_12 || !hours12_24) return null;

  const data: Record<string, number> = {};
  const cellsByPeriodStart: Record<string, MaritimeSeichesPeriodCell> = {};

  for (const periodKey of MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS) {
    const periodMeta = readTimeRangePeriod(forecast, periodKey);
    if (!periodMeta) continue;

    const seiches = findSeiches(forecastArea.periods[periodKey] ?? []);
    if (!seiches) continue;

    const periodStartKey = periodStartToTableKey(periodMeta.start);
    const value = seichesStatusToValue(seiches.status);
    data[periodStartKey] = value;

    const cell: MaritimeSeichesPeriodCell = { value };

    if (seiches.status === "applicable") {
      cell.applicableSubtype = normalizeSeichesSubtypeLabel(seiches.subtype);
      const message = resolveSeichesMessage(seiches.message, locale);
      if (message) {
        cell.message = message;
      }
    }

    cellsByPeriodStart[periodStartKey] = cell;
  }

  if (Object.keys(data).length === 0) return null;

  const series: Serie[] = [
    {
      name: areaName,
      description: null,
      standardName: null,
      createdAt: forecast.published_at,
      validUntil: forecast.next_forecast_at,
      type: "seiches",
      source: "maritime_forecast",
      sourceUrl: "",
      updateFrequency: "",
      timeResolution: SEICHES_TIME_RESOLUTION,
      maxTimeResolution: null,
      units: "",
      dims: "",
      data,
    },
  ];

  const timeRange = {
    timeStart: startOfHour(new Date(hours0_12.start)),
    timeEnd: new Date(hours12_24.end),
  } as TimeRange;

  return { series, timeRange, cellsByPeriodStart };
}

/** Area ids that include at least one seiches component in any forecast period. */
export function getMaritimeForecastAreaIdsWithSeiches(
  forecast: MaritimeForecast,
): string[] {
  return forecast.areas
    .filter((area) =>
      Object.values(area.periods).some((entries) =>
        (entries ?? []).some(
          (component: MaritimeForecastComponent) => component.type === "seiches",
        ),
      ),
    )
    .map((area) => area.area_id);
}

/**
 * Restricts coastal CMS area options to regions present in the coastal forecast
 * with seiches output. When `useFullListFallback` is true (forecast load failed),
 * returns all coastal areas unchanged.
 */
export function filterCoastalAreasForSeichesWidget(
  coastalAreas: MaritimeArea[],
  forecast: MaritimeForecast | undefined,
  useFullListFallback: boolean,
  preserveAreaId?: string,
): MaritimeArea[] {
  let result = coastalAreas;

  if (forecast && !useFullListFallback) {
    const withSeiches = new Set(getMaritimeForecastAreaIdsWithSeiches(forecast));
    result = coastalAreas.filter((area) => withSeiches.has(area.id));
  }

  if (preserveAreaId && !result.some((area) => area.id === preserveAreaId)) {
    const preserved = coastalAreas.find((area) => area.id === preserveAreaId);
    if (preserved) {
      result = [preserved, ...result];
    }
  }

  return result;
}
